import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Divider,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  AccountBalance,
  Receipt,
  Payment,
  TrendingUp,
  CheckCircle,
  AccountBalanceWallet,
  LocalAtm,
  Handshake,
  Groups,
} from '@mui/icons-material';

const PaymentFlowVisualization = () => {
  const [activeStep, setActiveStep] = useState(0);

  // Example scenario data
  const exampleScenario = {
    ticket: {
      id: 'TKT-2024-001',
      service: 'Trade License Renewal',
      govtCharge: 100,
      serviceCharge: 20,
      tax: 2,
      totalCost: 122,
    },
    wallet: {
      topupAmount: 1000,
      initialBalance: 500,
    },
    distribution: {
      servicePartner: 120,
      grossProfit: 2,
      channelCommissionRate: 20,
      channelCommission: 0.4, // 20% of gross profit (2)
      thaklisPortion: 1.6,
    },
  };

  const steps = [
    {
      label: 'Company Creates Ticket',
      icon: <Receipt />,
      description: 'Company 1 creates a service ticket with pricing breakdown',
      details: (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Charge Type</TableCell>
                <TableCell align="right">Amount (AED)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Government Charge</TableCell>
                <TableCell align="right">{exampleScenario.ticket.govtCharge}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Service Charge</TableCell>
                <TableCell align="right">{exampleScenario.ticket.serviceCharge}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Tax on Service</TableCell>
                <TableCell align="right">{exampleScenario.ticket.tax}</TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Total Cost</strong></TableCell>
                <TableCell align="right"><strong>{exampleScenario.ticket.totalCost}</strong></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ),
      route: 'Common Ticket → New Ticket',
    },
    {
      label: 'Thaklis Completes Service',
      icon: <CheckCircle />,
      description: 'Service partner completes the ticket with transaction details',
      details: (
        <Box sx={{ mt: 2 }}>
          <Alert severity="success">
            <AlertTitle>Ticket Completed</AlertTitle>
            Ticket #{exampleScenario.ticket.id} - Transaction number and supporting documents added
          </Alert>
        </Box>
      ),
      route: 'Ticket Details → Complete Section',
    },
    {
      label: 'Company Tops Up Wallet',
      icon: <AccountBalanceWallet />,
      description: 'Company transfers money to Thaklis wallet via bank transfer',
      details: (
        <Box sx={{ mt: 2 }}>
          <Paper sx={{ p: 2, backgroundColor: '#e3f2fd' }}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>Topup Request</strong>
            </Typography>
            <Typography variant="body2">Amount: AED {exampleScenario.wallet.topupAmount}</Typography>
            <Typography variant="body2">Method: Bank Transfer</Typography>
            <Typography variant="body2" color="warning.main">Status: Pending Approval</Typography>
          </Paper>
        </Box>
      ),
      route: 'Customer Wallet → Topup',
    },
    {
      label: 'Thaklis Approves Topup',
      icon: <Payment />,
      description: 'Admin reviews and approves the wallet topup request',
      details: (
        <Box sx={{ mt: 2 }}>
          <Paper sx={{ p: 2, backgroundColor: '#e8f5e9' }}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>Topup Approved</strong>
            </Typography>
            <Typography variant="body2">
              Previous Balance: AED {exampleScenario.wallet.initialBalance}
            </Typography>
            <Typography variant="body2">
              Topup Amount: AED {exampleScenario.wallet.topupAmount}
            </Typography>
            <Typography variant="body2">
              <strong>New Balance: AED {exampleScenario.wallet.initialBalance + exampleScenario.wallet.topupAmount}</strong>
            </Typography>
          </Paper>
        </Box>
      ),
      route: 'Admin → Payment Gateways → Topup Requests',
    },
    {
      label: 'Map Payment to Ticket',
      icon: <LocalAtm />,
      description: 'Company maps wallet balance to completed ticket',
      details: (
        <Box sx={{ mt: 2 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>Ticket Payment Mapping</strong>
            </Typography>
            <Typography variant="body2">
              Selected Ticket: {exampleScenario.ticket.id}
            </Typography>
            <Typography variant="body2">
              Amount Deducted: AED {exampleScenario.ticket.totalCost}
            </Typography>
            <Typography variant="body2">
              Remaining Balance: AED {exampleScenario.wallet.initialBalance + exampleScenario.wallet.topupAmount - exampleScenario.ticket.totalCost}
            </Typography>
          </Paper>
        </Box>
      ),
      route: 'Account → Wallet → Ticket Mapping Tab',
    },
    {
      label: 'Payment to Service Partner',
      icon: <Handshake />,
      description: 'Admin processes payment to service partner',
      details: (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell>Government Charge</TableCell>
                <TableCell align="right">AED {exampleScenario.ticket.govtCharge}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Service Charge</TableCell>
                <TableCell align="right">AED {exampleScenario.ticket.serviceCharge}</TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                <TableCell><strong>Total to Service Partner</strong></TableCell>
                <TableCell align="right">
                  <strong>AED {exampleScenario.distribution.servicePartner}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ),
      route: 'Admin → Partners → Service Partner Payments',
    },
    {
      label: 'Channel Partner Commission',
      icon: <Groups />,
      description: 'Calculate and pay channel partner commission',
      details: (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell>Customer Charge</TableCell>
                <TableCell align="right">AED {exampleScenario.ticket.totalCost}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Service Partner Cost</TableCell>
                <TableCell align="right">- AED {exampleScenario.distribution.servicePartner}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Gross Profit</strong></TableCell>
                <TableCell align="right">
                  <strong>AED {exampleScenario.distribution.grossProfit}</strong>
                </TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: '#f3e5f5' }}>
                <TableCell>Channel Commission ({exampleScenario.distribution.channelCommissionRate}%)</TableCell>
                <TableCell align="right">
                  AED {exampleScenario.distribution.channelCommission}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ),
      route: 'Admin → Finance → Commission Payments',
    },
    {
      label: 'Thaklis Net Profit',
      icon: <TrendingUp />,
      description: 'Final profit retained by Thaklis',
      details: (
        <Box sx={{ mt: 2 }}>
          <Paper sx={{ p: 3, backgroundColor: '#e8f5e9' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Profit Breakdown
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Gross Profit
                </Typography>
                <Typography variant="h6">
                  AED {exampleScenario.distribution.grossProfit}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Channel Commission
                </Typography>
                <Typography variant="h6" color="error">
                  - AED {exampleScenario.distribution.channelCommission}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Net Profit (Thaklis)
                </Typography>
                <Typography variant="h4" color="success.main">
                  AED {exampleScenario.distribution.thaklisPortion}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      ),
      route: 'Admin → Finance → Finance Dashboard',
    },
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  return (
    <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Header */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <AccountBalance sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      Complete Payment Flow
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      End-to-end visualization of ticket creation to payment distribution
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Summary Cards */}
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%', backgroundColor: '#e3f2fd' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Customer Pays
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  AED {exampleScenario.ticket.totalCost}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total ticket cost
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%', backgroundColor: '#fff3e0' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Service Partner Gets
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  AED {exampleScenario.distribution.servicePartner}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Govt + Service charge
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%', backgroundColor: '#f3e5f5' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Channel Commission
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  AED {exampleScenario.distribution.channelCommission}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {exampleScenario.distribution.channelCommissionRate}% of gross profit
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%', backgroundColor: '#e8f5e9' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Thaklis Net Profit
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  AED {exampleScenario.distribution.thaklisPortion}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Final profit retained
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Step-by-Step Flow */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h5" fontWeight="bold" mb={3}>
                  Step-by-Step Process
                </Typography>
                <Stepper activeStep={activeStep} orientation="vertical">
                  {steps.map((step, index) => (
                    <Step key={step.label}>
                      <StepLabel
                        StepIconComponent={() => (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              backgroundColor: index <= activeStep ? 'primary.main' : 'grey.300',
                              color: 'white',
                            }}
                          >
                            {step.icon}
                          </Box>
                        )}
                      >
                        <Box>
                          <Typography variant="h6">{step.label}</Typography>
                          <Chip
                            label={step.route}
                            size="small"
                            sx={{ mt: 0.5 }}
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      </StepLabel>
                      <StepContent>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                          {step.description}
                        </Typography>
                        {step.details}
                        <Box sx={{ mb: 2, mt: 2 }}>
                          <Button
                            variant="contained"
                            onClick={handleNext}
                            sx={{ mt: 1, mr: 1 }}
                            disabled={index === steps.length - 1}
                          >
                            {index === steps.length - 1 ? 'Finish' : 'Continue'}
                          </Button>
                          <Button
                            disabled={index === 0}
                            onClick={handleBack}
                            sx={{ mt: 1, mr: 1 }}
                          >
                            Back
                          </Button>
                        </Box>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
                {activeStep === steps.length && (
                  <Paper square elevation={0} sx={{ p: 3, mt: 2 }}>
                    <Alert severity="success">
                      <AlertTitle>Payment Flow Complete!</AlertTitle>
                      All payments have been distributed:
                      <ul>
                        <li>Service Partner received AED {exampleScenario.distribution.servicePartner}</li>
                        <li>Channel Partner received AED {exampleScenario.distribution.channelCommission}</li>
                        <li>Thaklis retained AED {exampleScenario.distribution.thaklisPortion}</li>
                      </ul>
                    </Alert>
                    <Button onClick={handleReset} sx={{ mt: 2 }} variant="contained">
                      Reset Flow
                    </Button>
                  </Paper>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Navigation */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h5" fontWeight="bold" mb={2}>
                  Quick Navigation to Related Pages
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <Receipt sx={{ fontSize: 40, color: 'primary.main' }} />
                      <Typography variant="subtitle1" mt={1}>
                        New Ticket
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Create service ticket
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <AccountBalanceWallet sx={{ fontSize: 40, color: 'primary.main' }} />
                      <Typography variant="subtitle1" mt={1}>
                        Wallet Topup
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Add funds to wallet
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <LocalAtm sx={{ fontSize: 40, color: 'primary.main' }} />
                      <Typography variant="subtitle1" mt={1}>
                        Ticket Mapping
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pay for completed tickets
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <TrendingUp sx={{ fontSize: 40, color: 'primary.main' }} />
                      <Typography variant="subtitle1" mt={1}>
                        Finance Dashboard
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        View financial metrics
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
    </Box>
  );
};

export default PaymentFlowVisualization;
